import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { useCustomers, nameOf } from "@/lib/lookups";
import { PageHeader, SectionTitle } from "@/components/common/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, CheckCircle2 } from "lucide-react";

const cents = (c) => fmtMoney((c || 0) / 100);
const inputCls = "border-[#2D3F62] bg-[#0B1120] text-slate-100 focus-visible:ring-cyan-500";
const RECON = ["Unreconciled", "Reconciled", "Needs Review"];

function Stat({ label, value, tone, testId }) {
  return (
    <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4" data-testid={testId}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`mt-1 text-xl font-semibold ${tone || "text-slate-100"}`}>{value}</div>
    </div>
  );
}

export default function Payments() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reconFilter, setReconFilter] = useState("");
  const [form, setForm] = useState({ invoice_id: "", amount: "", date: "", method: "Bank Transfer", reference: "", notes: "" });
  const { data: customers } = useCustomers();

  const { data: dash } = useQuery({ queryKey: ["pay-dashboard"], queryFn: async () => (await api.get("/payments/dashboard")).data });
  const { data: payments, isLoading } = useQuery({ queryKey: ["payments", reconFilter], queryFn: async () => (await api.get(`/payments${reconFilter ? `?reconciliation=${reconFilter}` : ""}`)).data });
  const { data: openInvoices } = useQuery({ queryKey: ["invoices-open"], queryFn: async () => (await api.get("/invoices?paid=false")).data });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["pay-dashboard"] }); qc.invalidateQueries({ queryKey: ["payments"] }); qc.invalidateQueries({ queryKey: ["invoices-open"] }); };

  const record = useMutation({
    mutationFn: async () => (await api.post("/payments", { ...form, amount: Number(form.amount) })).data,
    onSuccess: () => { invalidate(); setOpen(false); setForm({ invoice_id: "", amount: "", date: "", method: "Bank Transfer", reference: "", notes: "" }); toast.success("Payment recorded"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const recon = useMutation({
    mutationFn: async ({ id, status }) => (await api.post(`/payments/${id}/reconcile`, { status })).data,
    onSuccess: () => { invalidate(); toast.success("Reconciliation updated"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const selInv = (openInvoices || []).find((i) => i.id === form.invoice_id);

  return (
    <div data-testid="payments-page">
      <PageHeader title="Payments & Reconciliation" subtitle="Record and reconcile customer payments against the accounts-receivable ledger.">
        <Button onClick={() => setOpen(true)} className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid="record-payment-btn"><Plus className="mr-1.5 h-4 w-4" /> Record Payment</Button>
      </PageHeader>

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Stat label="Invoiced" value={cents(dash?.invoiced_cents)} testId="stat-invoiced" />
        <Stat label="Received" value={cents(dash?.received_cents)} tone="text-emerald-300" testId="stat-received" />
        <Stat label="Outstanding" value={cents(dash?.outstanding_cents)} tone="text-amber-300" testId="stat-outstanding" />
        <Stat label="Overdue" value={cents(dash?.overdue_cents)} tone="text-rose-300" testId="stat-overdue" />
        <Stat label="GST collected" value={cents(dash?.gst_collected_cents)} testId="stat-gst" />
      </div>

      <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4">
        <div className="mb-3 flex items-center justify-between">
          <SectionTitle>Payments {dash?.unreconciled_count ? <span className="ml-2 rounded-full bg-amber-500/20 px-2 py-0.5 text-xs text-amber-200">{dash.unreconciled_count} to reconcile</span> : null}</SectionTitle>
          <Select value={reconFilter || "all"} onValueChange={(v) => setReconFilter(v === "all" ? "" : v)}>
            <SelectTrigger className={`h-8 w-44 text-xs ${inputCls}`} data-testid="recon-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">
              <SelectItem value="all">All payments</SelectItem>
              {RECON.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-cyan-400" /></div>
        ) : !payments?.length ? (
          <p className="py-6 text-center text-sm text-slate-500" data-testid="payments-empty">No payments recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payments-table">
              <thead><tr className="text-left text-xs uppercase text-slate-500">
                <th className="py-2">Date</th><th>Invoice</th><th>Customer</th><th className="text-right">Amount</th><th>Method</th><th>Reference</th><th>Reconciliation</th>
              </tr></thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-t border-[#1E2D4A]" data-testid={`payment-row-${p.id}`}>
                    <td className="py-2 text-slate-300">{fmtDate(p.date)}</td>
                    <td className="font-mono-fin text-cyan-300">{p.invoice_number}</td>
                    <td className="text-slate-300">{nameOf(customers, p.customer_id)}</td>
                    <td className="text-right text-emerald-300">{cents(p.amount_cents)}</td>
                    <td className="text-slate-400">{p.method}</td>
                    <td className="text-slate-400">{p.reference || "—"}</td>
                    <td>
                      <Select value={p.reconciliation} onValueChange={(v) => recon.mutate({ id: p.id, status: v })}>
                        <SelectTrigger className={`h-7 w-40 text-xs ${inputCls}`} data-testid={`recon-select-${p.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">
                          {RECON.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg border-[#1E2D4A] bg-[#111A2E] text-slate-100" data-testid="record-payment-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-cyan-400" /> Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-400">Invoice *</Label>
              <Select value={form.invoice_id} onValueChange={(v) => { const inv = (openInvoices || []).find((i) => i.id === v); setForm({ ...form, invoice_id: v, amount: inv ? (inv.balance_cents / 100).toFixed(2) : "" }); }}>
                <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="pay-invoice-select"><SelectValue placeholder="Select an issued invoice" /></SelectTrigger>
                <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">
                  {(openInvoices || []).map((i) => <SelectItem key={i.id} value={i.id}>{i.number} · {nameOf(customers, i.customer_id)} · bal {cents(i.balance_cents)}</SelectItem>)}
                </SelectContent>
              </Select>
              {selInv && <p className="mt-1 text-xs text-slate-500">Balance due: {cents(selInv.balance_cents)}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-400">Amount received *</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="pay-amount-input" /></div>
              <div><Label className="text-xs text-slate-400">Payment date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="pay-date-input" /></div>
              <div><Label className="text-xs text-slate-400">Method</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="pay-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">
                    {["Bank Transfer", "Card", "Direct Debit", "Cash", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-slate-400">Reference</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="pay-reference-input" /></div>
            </div>
            <div><Label className="text-xs text-slate-400">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="pay-notes-input" /></div>
            <Button onClick={() => record.mutate()} disabled={!form.invoice_id || !form.amount || record.isPending} className="w-full bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid="pay-submit-btn">{record.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Record Payment</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
